/* **** RN 08/23: DEPRECATED ON UPRIGHT PLATFORM – DO NOT USE BOUCOLORS FOR ANYTHING NEW! USE PLATFORM-COLORS.TS INSTEAD. **** */

import {
  PlatformGrayScale,
  PlatformGreenScale,
  PlatformYellowScale,
  PlatformRedScale,
  PlatformImpactScale,
} from './platform-colors';

/**
 * COLOR USAGE INSTRUCTIONS:
 * In this file are all colors in use in the model. Colors should not be defined directly, but always using the BouColor object
 * exported in this file. This file has two sections: 1) Colors available 2) Colors by use case. The first section charts all the
 * colors available for use. The second section defines which kinds of use cases there are, and maps the colors from Section 1 to these
 * use cases.
 *
 * Please pick a use case closest to what you're trying to achieve, and use that color in your component. If there is no use case yet that
 * adequately describes your use case, feel free to create a new one.
 *
 */

/**
 * SECTION 1: COLORS AVAILABLE FOR USE. Do not use these directly in components. For usage, go to section 2.
 * NOTE OA 9/21: Due to changes in our TypeScript setup, these enums need to be exported for the BouColor object to work correctly. Still don't
 * use them directly in code.
 */

// Don't edit unless the colors actually change in the original design system
export enum BouFigmaMain {
  TEXT_AND_BACKGROUND_DARK = '#141414',
  HIGHLIGHT = '#FCFF5D',
  SUPPORTIVE1 = '#F9F9F9',
  SUPPORTIVE2 = '#EEEEEE',
  SUPPORTIVE3 = '#b3b3b3',
  TEXT_AND_BACKGROUND_LIGHT = '#FFFFFF',
}

// Don't edit unless the colors actually change in the original design system
enum BouFigmaTool {
  NEGATIVE = '#7400B8',
  POSITIVE = '#42E7B4',
  NEUTRAL = '#48BFE3',
  // TODO can this be removed?
  SUPPORT1 = '#FF61AD',
  SUPPORT2 = '#F72585',
  DISABLED = '#EEEEEE', // Same as BouFigmaMAIN.SUPPORTIVE2
}

// TODO can this be removed?
// Don't edit unless the colors actually change in the original design system
enum BouFigmaValenceGradient {
  NEG5 = '#7400B8', // Same as BouFigmaTool.NEGATIVE
  NEG4 = '#6930C3',
  NEG3 = '#5E60CE',
  NEG2 = '#5390D9',
  NEG1 = '#4EA8DE',
  POS1 = '#48BFE3', // Same as BouFigmaTool.NEUTRAL
  POS2 = '#56CFE1',
  POS3 = '#64DFDF',
  POS4 = '#72EFDD',
  POS5 = '#80FFDB',
}

export enum BouComparisonColor {
  _1 = '#240038',
  // TODO can this be removed?
  _2 = '#FF9DCC',
  _3 = '#97B8FF',
  _4 = '#FFDC91',
  _5 = '#E8A184',
  _6 = '#C4C4C4',
}

// Shades of gray for different uses. Add new ones only if current ones won't do for some reason.
export enum UprightGrayscale {
  _000000 = '#000000',
  _141414 = '#141414', // Same as BouFigmaMain.TEXT_AND_BACKGROUND_DARK
  _292929 = '#292929',
  _525252 = '#525252',
  _696969 = '#696969',
  _696969_ALPHA = 'rgba(255, 255, 255, 0.3)',
  _8F8F8F = '#8F8F8F',
  _B3B3B3 = '#B3B3B3', // Same as BouFigmaMain.SUPPORTIVE3
  _D0D7DE = '#D0D7DE', // Smoother border color than the above _B3B3B3
  _BFBFBF = '#BFBFBF',
  _BFBFBF_ALPHA = 'rgba(0, 0, 0, 0.25)', // To be used on a dark background
  _C4C4C4 = '#C4C4C4', // Same as BouComparisonColor._7
  _D4D4D4 = '#D4D4D4',
  _E7E7E7 = '#E7E7E7',
  _EEEEEE = '#EEEEEE', // Same as BouFigmaMain.SUPPORTIVE2 & BouFigmaTool.DISABLED,
  _F6F8FA = '#F6F8FA',
  _F2F2F2 = '#F2F2F2',
  _F9F9F9 = '#F9F9F9', // Same as BouFigmaMain.SUPPORTIVE1
  // TODO can this be removed?
  _FCFCFC = '#FCFCFC',
  _FFFFFF = '#FFFFFF', // Same as BouFigmaMain.TEXT_AND_BACKGROUND_LIGHT
}

// If you need to add a new color that doesn't fit any of the enums above, put it here. Though do think twice if you really cannot
// do with one of the colors we already have.
export enum UprightCustom {
  HIGHLIGHT_LIGHT_YELLOW = '#ffffcd',
  ERROR_RED = 'firebrick',
  SDG_STRONGLY_ALIGNED_GREEN = '#00CC9B',
  SDG_ALIGNED_GREEN = '#00FFC2',
  SDG_WEAKLY_ALIGNED_GREEN = '#d2fcf2', // was 99FFE6
  SDG_MISALIGNED_PURPLE = '#B433FF',
  SDG_STRONGLY_MISALIGNED_PURPLE = '#7400B8',
  SDG_WEAKLY_MISALIGNED_PURPLE = '#CEA6E6',
  EXPERT_MODE_HIGHLIGHT = '#2222BB',
  NATURE_DIRECT_DISCLOSURE = '#1D9BF0',
  NATURE_OTHER = '#969696',
}

enum CompanyPlatformColor {
  _FFEE5F = '#ffee5f',
  _FAFAFA = '#FAFAFA',
  _F5F5F5 = '#F5F5F5',
  _EAEAEA = '#EAEAEA',
  _E5E5E5 = '#E5E5E5',
  _BG_LIGHT_PURPLE = 'hsla(244, 100%, 81%, 0.4)',
  _A59EFF = '#a59eff',
  // TODO can this be removed?
  _FF2E3F = '#FF2E3F',
}

enum StateColors {
  SUCCESS = '#00D563',
  SUCCESS_DARK = '#107e3e',
  SUCCESS_LIGHT = '#dfffde',
  WARNING = '#FFC962',
  WARNING_DARK = '#a16207',
  WARNING_LIGHT = '#fefce8',
  WARNING_BORDER = '#eeeac8',
  DANGER = '#FF6262',
  DANGER_DARK = '#a31a0a',
  DANGER_LIGHT = '#ffeeee',
}

/**
 * SECTION 2: USE CASES WITH COLORS MAPPED.
 *
 * Please pick a use case closest to what you're trying to achieve, and use that color in your component. If there is no use case yet that
 * adequately describes your use case, feel free to create a new one. Please use the same three-part naming convention that is used in existing colors:
 * 1. SET IDENTIFIER: same for all colors in the same set. For example TOOL.
 * 2. USE CASE: where this color should be used. For example HISTOGRAM_HILIGHT.
 * 3. COLOR: approximate wording on what the color looks like. For example LIGHT_BLUE.
 * The examples above would form a name TOOL_HISTOGRAM_HILIGHT_LIGHT_BLUE.
 */

// RN 24.3.2023: New platform color utilities. To be cleaned up and adopted after ODAI.
const PlatformColor = {
  // Background color utilities:
  PLATFORM_BG_WHITE: PlatformGrayScale.WHITE,
  PLATFORM_BG_GRAY_LIGHTEST: PlatformGrayScale.GRAY_000,
  PLATFORM_BG_GRAY_LIGHTER: PlatformGrayScale.GRAY_100,
  PLATFORM_BG_GRAY_LIGHT: PlatformGrayScale.GRAY_200,
  PLATFORM_BG_GRAY: PlatformGrayScale.GRAY_600,
  PLATFORM_BG_GRAY_DARK: PlatformGrayScale.GRAY_800,
  PLATFORM_BG_BLACK: PlatformGrayScale.BLACK,
  // Border color utilities:
  PLATFORM_BORDER_GRAY_LIGHTER: PlatformGrayScale.GRAY_200,
  PLATFORM_BORDER_GRAY_LIGHT: PlatformGrayScale.GRAY_300,
  PLATFORM_BORDER_GRAY: PlatformGrayScale.GRAY_400,
  PLATFORM_BORDER_GRAY_DARK: PlatformGrayScale.GRAY_700,
  PLATFORM_BORDER_GREEN: PlatformGreenScale.GREEN_500,
  PLATFORM_BORDER_YELLOW: PlatformYellowScale.YELLOW_500,
  PLATFORM_BORDER_RED: PlatformRedScale.RED_500,
  // Text color utilities:
  PLATFORM_TEXT_BLACK: PlatformGrayScale.BLACK,
  PLATFORM_TEXT_GRAY_DARK: PlatformGrayScale.GRAY_900,
  PLATFORM_TEXT_GRAY: PlatformGrayScale.GRAY_600,
  PLATFORM_TEXT_GRAY_LIGHT: PlatformGrayScale.GRAY_400,
  PLATFORM_TEXT_GRAY_LIGHTER: PlatformGrayScale.GRAY_200,
  PLATFORM_TEXT_WHITE: PlatformGrayScale.WHITE,
  // State color utilities:
  PLATFORM_DISABLED_GRAY: PlatformGrayScale.GRAY_400,
  // Impact color utilities:
  PLATFORM_IMPACT_POS_STRONG: PlatformImpactScale.POSITIVE_STRONG,
  PLATFORM_IMPACT_POS_MODERATE: PlatformImpactScale.POSITIVE_MODERATE,
  PLATFORM_IMPACT_POS_WEAK: PlatformImpactScale.POSITIVE_WEAK,
  PLATFORM_IMPACT_NEG_STRONG: PlatformImpactScale.NEGATIVE_STRONG,
  PLATFORM_IMPACT_NEG_MODERATE: PlatformImpactScale.NEGATIVE_MODERATE,
  PLATFORM_IMPACT_NEG_WEAK: PlatformImpactScale.NEGATIVE_WEAK,
  // Icon color utilities:
  PLATFORM_ICON_WHITE: PlatformGrayScale.WHITE,
  PLATFORM_ICON_GRAY: PlatformGrayScale.GRAY_400,
  PLATFORM_ICON_GRAY_DARK: PlatformGrayScale.GRAY_800,
  // Brand color utilities: // RN: Perhaps separate from plaform into BrandColor
  PLATFORM_BRAND_YELLOW: BouFigmaMain.HIGHLIGHT,
} as const;

// Used in facade
const FacadeColor = {
  FACADE_BORDER_BLACK: UprightGrayscale._141414,
  FACADE_BORDER_GRAY: UprightGrayscale._BFBFBF_ALPHA,
  FACADE_BORDER_WHITE: UprightGrayscale._FFFFFF,
  FACADE_TEXT_BLACK: UprightGrayscale._141414,
} as const;

// Used in uprightplatform.com
const ToolColor = {
  TOOL_BACKGROUND_MUTED_BLACK: '#2c2c2c',
  TOOL_BACKGROUND_MUTED_DARK_GRAY: '#484a4d',
  TOOL_BACKGROUND_WHITE: UprightGrayscale._FFFFFF,
  TOOL_BACKGROUND_SUPERLIGHT_GRAY: CompanyPlatformColor._FAFAFA,
  TOOL_BACKGROUND_LIGHT_GRAY: CompanyPlatformColor._F5F5F5,
  TOOL_BACKGROUND_LIGHT_GRAY_HOVER: CompanyPlatformColor._EAEAEA,
  TOOL_BACKGROUND_LIGHT_GRAY_ACTIVE: CompanyPlatformColor._E5E5E5,
  TOOL_BACKGROUND_LIGHT_PURPLE: CompanyPlatformColor._BG_LIGHT_PURPLE,
  TOOL_BACKGROUND_BLUISH_PURPLE: CompanyPlatformColor._A59EFF,
  TOOL_BORDER_BLACK: UprightGrayscale._141414,
  TOOL_BORDER_DARK_GRAY: UprightGrayscale._E7E7E7, // Note: this is actually lighter than the below TOOL_BORDER_LIGHT_GRAY
  TOOL_BORDER_CARDS_LIGHT_GRAY: UprightGrayscale._D0D7DE,
  TOOL_BORDER_LIGHT_GRAY: UprightGrayscale._B3B3B3,
  TOOL_BORDER_LIGHTER_GRAY: UprightGrayscale._EEEEEE,
  TOOL_BUTTON_BACKGROUND_ACTIVE_GRAY: UprightGrayscale._8F8F8F,
  TOOL_BUTTON_BACKGROUND_DARK: UprightGrayscale._141414,
  TOOL_BUTTON_BACKGROUND_DARK_HOVER: UprightGrayscale._292929,
  TOOL_BUTTON_BACKGROUND_DISABLED_GRAY: UprightGrayscale._F9F9F9,
  TOOL_BUTTON_BACKGROUND_LIGHT: UprightGrayscale._F6F8FA,
  TOOL_BUTTON_BACKGROUND_LIGHT_HOVER: UprightGrayscale._F2F2F2,
  TOOL_BUTTON_BACKGROUND_YELLOW: CompanyPlatformColor._FFEE5F,
  TOOL_BUTTON_TEXT_DISABLED_GRAY: UprightGrayscale._C4C4C4,
  TOOL_SUCCESS_GREEN: StateColors.SUCCESS,
  TOOL_SUCCESS_GREEN_DARK: StateColors.SUCCESS_DARK,
  TOOL_SUCCESS_GREEN_LIGHT: StateColors.SUCCESS_LIGHT,
  TOOL_WARNING_YELLOW: StateColors.WARNING,
  TOOL_WARNING_YELLOW_DARK: StateColors.WARNING_DARK,
  TOOL_WARNING_YELLOW_LIGHT: StateColors.WARNING_LIGHT,
  TOOL_WARNING_YELLOW_BORDER: StateColors.WARNING_BORDER,
  TOOL_DANGER_RED: StateColors.DANGER,
  TOOL_DANGER_RED_DARK: StateColors.DANGER_DARK,
  TOOL_DANGER_RED_LIGHT: StateColors.DANGER_LIGHT,
  TOOL_DISABLED_GRAY: UprightGrayscale._BFBFBF,
  TOOL_FRAME_LIGHT_GRAY: UprightGrayscale._F9F9F9,
  TOOL_HIGHLIGHT_BRIGHT_YELLOW: BouFigmaMain.HIGHLIGHT,
  TOOL_HISTOGRAM_HIGHLIGHT_BLUE: BouFigmaTool.NEUTRAL,
  TOOL_HOVER_DARK_GRAY: UprightGrayscale._696969,
  TOOL_HOVER_LIGHT_GRAY: UprightGrayscale._B3B3B3,
  TOOL_INPUT_BORDER_DARK_GRAY: UprightGrayscale._C4C4C4,
  TOOL_OUTLINE_DISABLED_GRAY: UprightGrayscale._BFBFBF_ALPHA,
  TOOL_MODEL_VERSION_DISCLAIMER_GRAY: UprightGrayscale._B3B3B3,
  TOOL_TABLE_BACKGROUND_LIGHT_GRAY: UprightGrayscale._F9F9F9,
  TOOL_TABLE_HOVER_GRAY: UprightGrayscale._E7E7E7,
  TOOL_TABLE_SELECTED_YELLOW: UprightCustom.HIGHLIGHT_LIGHT_YELLOW,
  TOOL_TABLE_TEXT_DISABLED_GRAY: UprightGrayscale._8F8F8F,
  TOOL_TEXT_BLACK: UprightGrayscale._141414,
  TOOL_TEXT_DARKER_GRAY: UprightGrayscale._525252,
  TOOL_TEXT_DARK_GRAY: UprightGrayscale._696969,
  TOOL_TEXT_DISABLED_GRAY: UprightGrayscale._BFBFBF,
  TOOL_TEXT_ERROR_RED: UprightCustom.ERROR_RED,
  TOOL_TEXT_GRAY: UprightGrayscale._D4D4D4,
  TOOL_TEXT_HIGHLIGHT_YELLOW: UprightCustom.HIGHLIGHT_LIGHT_YELLOW,
  TOOL_TEXT_LIGHT_GRAY: UprightGrayscale._8F8F8F,
  TOOL_TEXT_WHITE: UprightGrayscale._FFFFFF,
  TOOL_TOGGLE_OFF_GRAY: UprightGrayscale._C4C4C4,
  TOOL_TOGGLE_ON_BLACK: UprightGrayscale._141414,
  TOOL_EXPERT_MODE_HIGHLIGHT_BLUE: UprightCustom.EXPERT_MODE_HIGHLIGHT,
} as const;

const ValueSetSelectorColor = {
  VALUE_SET_SLIDER_FILLED_DARK_GRAY: UprightGrayscale._696969,
  VALUE_SET_SLIDER_KNOB_HIGHLIGHT_BLACK: UprightGrayscale._141414,
  VALUE_SET_SLIDER_UNFILLED_LIGHT_GRAY: UprightGrayscale._EEEEEE,
} as const;

const TimeSeriesLineChartColor = {
  TIME_SERIES_HOVERED_POINT_GRAY: UprightGrayscale._696969,
  TIME_SERIES_POINT_BLACK: UprightGrayscale._141414,
  TIME_SERIES_SELECTED_POINT_RECTANGLE_GRAY: BouFigmaMain.SUPPORTIVE1,
  TIME_SERIES_ZERO_LINE_GRAY: UprightGrayscale._D4D4D4,
} as const;

// Impact chart & other impact related
const ImpactColor = {
  IMPACT_BACKGROUND_GRAY: UprightGrayscale._F9F9F9,
  IMPACT_BORDER_GRAY: UprightGrayscale._D4D4D4,
  IMPACT_COLUMN_TITLE_GRAY: UprightGrayscale._696969,
  IMPACT_GENERIC_GRAY: UprightGrayscale._8F8F8F,
  IMPACT_NEGATIVE_PURPLE: BouFigmaTool.NEGATIVE,
  IMPACT_NEUTRAL_GRAY: UprightGrayscale._BFBFBF,
  IMPACT_POSITIVE_GREEN: BouFigmaTool.POSITIVE,
  IMPACT_SCORE_TEXT_BLACK: UprightGrayscale._141414,
} as const;

const SdgAlignmentColor = {
  SDG_STRONGLY_ALIGNED_GREEN: UprightCustom.SDG_STRONGLY_ALIGNED_GREEN,
  SDG_ALIGNED_GREEN: UprightCustom.SDG_ALIGNED_GREEN,
  SDG_MISALIGNED_PURPLE: UprightCustom.SDG_MISALIGNED_PURPLE,
  SDG_STRONGLY_MISALIGNED_PURPLE: UprightCustom.SDG_STRONGLY_MISALIGNED_PURPLE,
  SDG_WEAKLY_ALIGNED_GREEN: UprightCustom.SDG_WEAKLY_ALIGNED_GREEN,
  SDG_WEAKLY_MISALIGNED_PURPLE: UprightCustom.SDG_WEAKLY_MISALIGNED_PURPLE,
};

const MetricNatureColor = {
  NATURE_DIRECT_DISCLOSURE: UprightCustom.NATURE_DIRECT_DISCLOSURE,
  NATURE_OTHER: UprightCustom.NATURE_OTHER,
};

// Do not use LegacyColors if possible. Instead just create a new color
// in one of Facade/Tool/Impact/YourOwn categories, and give it a name describing your
// use case.
const LegacyColor = {
  BLACK_TOTAL: UprightGrayscale._000000,
  BLACK: BouFigmaMain.TEXT_AND_BACKGROUND_DARK,
  DARK_GRAY_ALPHA: UprightGrayscale._BFBFBF_ALPHA,
  DARK_GRAY_NO_ALPHA: UprightGrayscale._BFBFBF,
  DARK_GRAY_ON_BLACK_ALPHA: UprightGrayscale._696969_ALPHA,
  DARK_GRAY_ON_BLACK_NO_ALPHA: UprightGrayscale._696969,
  GRAY: BouFigmaMain.SUPPORTIVE2,
  GREY_B3B3B3: BouFigmaMain.SUPPORTIVE3,
  LIGHT_GRAY: BouFigmaMain.SUPPORTIVE1,
  WHITE: BouFigmaMain.TEXT_AND_BACKGROUND_LIGHT,
  YELLOW: BouFigmaMain.HIGHLIGHT,
} as const;

// BouColor can be used almost like an enum, and it includes all the colors above.
// If you want to limit a type to only certain colors, you can do it as follows:
// const color: typeof BouColor.TOOL_TEXT_GRAY | typeof BouColor.TOOL_TEXT_BLACK;

/**
 * @deprecated on the platform since 10/2023
 * For all colors, reference customTheme via themeGet or useTheme.
 */
export const BouColor = {
  ...LegacyColor,
  ...PlatformColor,
  ...ToolColor,
  ...ImpactColor,
  ...FacadeColor,
  ...TimeSeriesLineChartColor,
  ...ValueSetSelectorColor,
  ...SdgAlignmentColor,
  ...MetricNatureColor,
} as const;

export type BouColor = (typeof BouColor)[keyof typeof BouColor];

export const bouComparisonColors = Object.values(BouComparisonColor);

// Without doing this hacky export, the code won't build if one of the enums is unused.
export const suppressWarningsOfUnusedDeclarations =
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  BouFigmaMain && BouFigmaTool && BouFigmaValenceGradient && UprightGrayscale && UprightCustom;
